import { useState } from 'react';

import { useParams } from 'react-router-dom';

import { List, Modal, notification, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';

import { CustomNotification, UploadFile } from 'common/components';

import { IMPORT_QA_SPEC_BULK_DATA } from 'services/product/endpoints';
import { UPLOAD_MAX_SIZE } from 'static/Constants';
import { useDispatchReloadPage } from 'hooks/useReloadPage';
import { useProductFullView } from 'hooks/useProductFullView';

import {
  getKeyFactPanels,
  getKeySupplementPanels,
} from 'pages/product-full-view/components/product-media-area/multiple-panel/facts-panel/useQueryFactsPanel';

import { groupProductData } from 'pages/product-full-view/components/product-media-area/shared/hooks';

import { useReloadDocumentCertificateTab } from 'hooks/useReloadDocumentCertificateTab';

import { useIntl } from 'react-intl';
import Messages from 'i18n/messages/product';
import { useRefetchQaSpecData } from 'hooks/qa-spec/useRefetchQaSpecData';
import { useGetDataProductItemModules } from 'pages/product-full-view/shared/hooks';
import { ConfirmContent } from 'pages/asset-full-view/components';

const { Text, Paragraph } = Typography;

const ImportQaSpecBulkEditModal = (props) => {
  const intl = useIntl();
  const reloadPage = useDispatchReloadPage();
  const { reloadDocumentCertificateTab } = useReloadDocumentCertificateTab();

  const queryClient = useQueryClient();
  const { visible, setVisible, isProductDetail } = props;

  const { id } = useParams();

  const [errorList, setErrorList] = useState([]);

  const productId = isProductDetail ? id : null;

  const { productFull, reloadProductFullView } = useProductFullView({
    productId,
  });

  const { refetchDataProductModules } = useGetDataProductItemModules(
    productId,
    false
  );

  const refetchQaSpecData = useRefetchQaSpecData();

  const onCancel = () => {
    closeModal();
    setErrorList([]);
  };

  const closeModal = () => {
    setVisible(false);
  };

  const handleResponseUpload = (response) => {
    setErrorList([]);
    if (response?.data) {
      if (response?.data?.isSuccess) {
        const systemMessage = response?.data?.message;
        CustomNotification.success(
          systemMessage ||
          intl.formatMessage(Messages.importProductBulkEditSuccess)
        );
        // ensure full page reload so grids in other tabs refresh correctly
        // the reloadPage dispatch wasn't sufficient in some cases
        window.location.reload();

        if (productFull?.productId) {
          queryClient.invalidateQueries({
            queryKey: getKeySupplementPanels(productFull?.productId),
          });
          queryClient.invalidateQueries({
            queryKey: getKeyFactPanels(productFull?.productId),
          });

          queryClient.invalidateQueries({
            queryKey: groupProductData.getAllGroups(productId),
          });

          reloadProductFullView();

          reloadDocumentCertificateTab();

          refetchQaSpecData();

          refetchDataProductModules();
        }

        closeModal();
      } else {
        notification.error({
          message:
            response?.message ||
            intl.formatMessage(Messages.importProductBulkEditError),
        });
        if (response?.data?.validateFailDetails) {
          setErrorList(response?.data?.validateFailDetails);
        }
      }
    }
  };

  const renderErrorList = () => {
    return (
      <div className='scroller' style={{ height: 200, paddingTop: '8px' }}>
        <List
          header={
            <Text style={{ fontWeight: 700 }} type='danger'>
              <InfoCircleOutlined /> Error
            </Text>
          }
          bordered
          dataSource={errorList}
          renderItem={(errorItem) => {
            const errorMessage = errorItem.reason || 'Invalid data';
            return (
              <List.Item>
                <Paragraph
                  type='danger'
                  style={{
                    marginBottom: 0,
                  }}
                >
                  <Text strong type='danger'>
                    {errorMessage}
                  </Text>{' '}
                  in{' '}
                  <Text strong type='danger'>
                    {errorItem?.sheetName}
                  </Text>{' '}
                  sheet, on row{' '}
                  <Text strong type='danger'>
                    {errorItem?.row}
                  </Text>
                  , column{' '}
                  <Text strong type='danger'>
                    {errorItem?.column}
                  </Text>
                </Paragraph>
              </List.Item>
            );
          }}
        />
      </div>
    );
  };

  const initFormValue = {
    defaultStatus: 'Pending',
    isCreateThumbs: true,
    autoApplyOrReplaceOnProducts: false,
  };
  const [multiImportFormValue, setMultiImportFormValue] =
    useState(initFormValue);

  return (
    <Modal
      visible={visible}
      title={intl.formatMessage(Messages.importQaSpecBulkEditTitle)}
      onCancel={onCancel}
      footer={null}
      width={1000}
      destroyOnClose
    >
      <div style={{ height: 450, overflow: 'hidden' }}>
        <UploadFile
          uploadAPL //* for getting error list
          multiple={true}
          multipleImport={true}
          uploadIndividually={true}
          isBase64={false}
          showMultiple={true}
          supportTypes={['xlsx', 'xls']}
          showXlsTemplate={false}
          apiUrl={IMPORT_QA_SPEC_BULK_DATA}
          showUploadSuccessMsg={false}
          showServerMessage={false}
          notShowMessage={true}
          getResponse={(response) => handleResponseUpload(response)}
          maxSize={UPLOAD_MAX_SIZE.GENERAL}
          onUploadComplete={onCancel}
        />
      </div>
      {errorList?.length ? renderErrorList() : null}
    </Modal>
  );
};

export default ImportQaSpecBulkEditModal;
